"use client";

import { useAppSelector } from "@/store/hooks";
import { DefaultBackground } from "@/ui";
import { useMemo, useState } from "react";
import InstagramHeader from "./InstagramHeader";
import InstagramLoadMore from "./InstagramLoadMore";
import InstagramPostCard from "./InstagramPostCard";

export default function InstagramFeed() {
  const { data, loading, error } = useAppSelector((state) => state.content);
  const [visiblePosts, setVisiblePosts] = useState(8);
  const [loadingMore, setLoadingMore] = useState(false);

  const decorativeElements = useMemo(
    () => [
      {
        size: "md" as const,
        position: { top: "16%", right: "6%" },
        opacity: 0.1,
      },
      {
        size: "sm" as const,
        position: { top: "64%", left: "8%" },
        opacity: 0.15,
      },
      {
        size: "lg" as const,
        position: { bottom: "32%", right: "10%" },
        opacity: 0.12,
      },
      {
        size: "sm" as const,
        position: { top: "33%", right: "25%" },
        opacity: 0.08,
      },
    ],
    [],
  );

  const handleLoadMore = () => {
    setLoadingMore(true);

    // Simulate API call
    setTimeout(() => {
      setVisiblePosts((prev) =>
        Math.min(prev + 4, data?.instagram?.posts.length || 0),
      );
      setLoadingMore(false);
    }, 1000);
  };

  // Loading state
  if (loading) {
    return (
      <DefaultBackground
        decorativeElements={decorativeElements}
        positionMode="absolute"
        fullHeight={false}
      >
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="animate-pulse space-y-8">
              <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mx-auto" />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[...Array(8)].map((_, i) => (
                  <div
                    key={i}
                    className="h-64 bg-gray-200 dark:bg-gray-700 rounded"
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
      </DefaultBackground>
    );
  }

  // Error or no data
  if (error || !data?.instagram) {
    return null;
  }

  const displayedPosts = data.instagram.posts.slice(0, visiblePosts);
  const hasMorePosts = visiblePosts < data.instagram.posts.length;

  return (
    <DefaultBackground
      decorativeElements={decorativeElements}
      positionMode="absolute"
      fullHeight={false}
    >
      <section className="py-20 overflow-hidden">
        {/* Header */}
        <InstagramHeader stats={data.instagram.stats} />

        {/* Masonry Layout */}
        <div className="max-w-7xl mx-auto px-6">
          <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
            {displayedPosts.map((post) => (
              <InstagramPostCard key={post.id} post={post} />
            ))}
          </div>

          {/* Load More Button */}
          {hasMorePosts && (
            <InstagramLoadMore
              onLoadMore={handleLoadMore}
              loading={loadingMore}
            />
          )}
        </div>
      </section>
    </DefaultBackground>
  );
}
